"use client";

import React, { useEffect } from "react";
import { notifyError, startBugsnag, leaveBreadcrumb } from "@/lib/bugsnag";

class FrontendErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; errorInfo?: string }
> {
  state = { hasError: false, errorInfo: undefined };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Extract component name from stack if available
    const componentName = this.extractComponentName(info.componentStack ?? undefined);
    
    notifyError(error, {
      source: "react-error-boundary",
      component: componentName,
      componentStack: info.componentStack ?? undefined,
      errorBoundary: "FrontendErrorBoundary",
    });

    this.setState({ errorInfo: error.message });
  }

  private extractComponentName(stack?: string): string {
    if (!stack) return "Unknown";
    // Try to extract first component name from stack
    const match = stack.match(/^\s+in (\w+)/);
    return match?.[1] ?? "Unknown";
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-black text-white">
          <div className="max-w-md text-center space-y-3">
            <h1 className="text-lg font-semibold">Something went wrong</h1>
            <p className="text-sm text-white/70">
              Please refresh the page. If the issue persists, contact support.
            </p>
            {process.env.NODE_ENV === "development" && this.state.errorInfo && (
              <p className="text-xs text-red-400 mt-2 font-mono">{this.state.errorInfo}</p>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function BugsnagProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    startBugsnag();

    // Track navigation for breadcrumbs
    const handleNavigationStart = () => {
      leaveBreadcrumb("Navigation started", {
        url: window.location.href,
      }, "navigation");
    };

    const handleNavigationComplete = () => {
      leaveBreadcrumb("Navigation complete", {
        url: window.location.href,
        path: window.location.pathname,
      }, "navigation");
    };

    // Track clicks for breadcrumbs
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();
      
      // Only track meaningful clicks (buttons, links, etc.)
      if (["a", "button", "input"].includes(tagName)) {
        const label = 
          target.getAttribute("aria-label") ||
          target.getAttribute("title") ||
          target.textContent?.trim().slice(0, 50) ||
          `${tagName} element`;

        leaveBreadcrumb(`Clicked: ${label}`, {
          tagName,
          id: target.id || undefined,
          className: target.className || undefined,
        }, "user");
      }
    };

    // Track form submissions
    const handleSubmit = (event: SubmitEvent) => {
      const form = event.target as HTMLFormElement;
      const formName = form.getAttribute("name") || form.id || "unnamed";
      
      leaveBreadcrumb(`Form submitted: ${formName}`, {
        action: form.action,
        method: form.method,
      }, "user");
    };

    // Enhanced error handler with better context
    const handleError = (event: ErrorEvent) => {
      const errorMessage = event.error instanceof Error 
        ? event.error.message 
        : event.message || "Unhandled error";

      notifyError(event.error || errorMessage, {
        source: "window.onerror",
        file: event.filename,
        line: event.lineno,
        column: event.colno,
        message: event.message,
        type: event.error ? event.error.constructor.name : "Unknown",
      });
    };

    // Enhanced rejection handler
    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      
      let errorMessage = "Unhandled promise rejection";
      if (reason instanceof Error) {
        errorMessage = reason.message;
      } else if (typeof reason === "string") {
        errorMessage = reason;
      } else if (reason && typeof reason === "object") {
        errorMessage = JSON.stringify(reason);
      }

      const error = reason instanceof Error ? reason : new Error(errorMessage);
      
      notifyError(error, {
        source: "unhandledrejection",
        reasonType: typeof reason,
        isError: reason instanceof Error,
        rawReason: reason && typeof reason === "object" ? JSON.stringify(reason) : String(reason),
      });
    };

    // Add event listeners
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);
    window.addEventListener("click", handleClick, true);
    document.addEventListener("submit", handleSubmit, true);
    
    // Track initial page load
    leaveBreadcrumb("Page loaded", {
      url: window.location.href,
      path: window.location.pathname,
      referrer: document.referrer,
    }, "navigation");

    // Track route changes (for Next.js client-side navigation)
    const observer = new MutationObserver(() => {
      handleNavigationComplete();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
      window.removeEventListener("click", handleClick, true);
      document.removeEventListener("submit", handleSubmit, true);
      observer.disconnect();
    };
  }, []);

  return <FrontendErrorBoundary>{children}</FrontendErrorBoundary>;
}
